/**
 * Resolve activity component / checkbox option labels for Data Event Analysis UI.
 * @param {Array<{id?: string, type?: string, content?: object}>} listItems
 * @returns {Map<string, object>}
 */
export function buildDataEventComponentIndex(listItems) {
  const map = new Map();
  if (!Array.isArray(listItems)) return map;
  for (const item of listItems) {
    if (!item || typeof item !== "object") continue;
    const id = item.id != null ? String(item.id) : null;
    const contentKey =
      item.content != null &&
      typeof item.content === "object" &&
      item.content.key != null &&
      String(item.content.key).trim() !== ""
        ? String(item.content.key)
        : null;
    // eventData / userData keys follow content.key; list row id may differ (e.g. reused fields)
    if (id) map.set(id, item);
    if (contentKey && contentKey !== id) map.set(contentKey, item);
    if (!id && contentKey) map.set(contentKey, item);
  }
  return map;
}

export function getDataEventFieldLabel(fieldKey, index) {
  if (!fieldKey) return "";
  const comp = index?.get(String(fieldKey));
  const label = comp?.content?.label;
  return typeof label === "string" && label.trim() !== ""
    ? label.trim()
    : fieldKey;
}

/**
 * Min/max for chart Y-axis from the field’s component definition (e.g. slider 0–10).
 * @returns {{ min: number, max: number } | null}
 */
export function getNumericFieldAxisBounds(fieldKey, componentIndex) {
  const comp = fieldKey ? componentIndex?.get(String(fieldKey)) : null;
  if (!comp?.type || !comp.content || typeof comp.content !== "object") {
    return null;
  }
  if (comp.type === "slider") {
    const min = Number(comp.content.min ?? 0);
    const max = Number(comp.content.max ?? 100);
    if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
      return null;
    }
    return { min, max };
  }
  return null;
}

export function getCheckboxOptionLabel(fieldKey, optionKey, index) {
  const comp = index?.get(String(fieldKey));
  if (comp?.type !== "checkbox" || !Array.isArray(comp?.content?.options)) {
    return optionKey;
  }
  const opt = comp.content.options.find(
    (o) =>
      o &&
      ((o.key != null && String(o.key) === String(optionKey)) ||
        (o.id != null && String(o.id) === String(optionKey))),
  );
  const lb = opt?.label;
  return typeof lb === "string" && lb.trim() !== "" ? lb.trim() : optionKey;
}

/**
 * Selected checkbox labels in schema order (same as the form), then orphan keys sorted.
 */
function formatCheckboxSelectionForDisplay(fieldKey, value, index) {
  const comp = index?.get(String(fieldKey));
  if (comp?.type !== "checkbox" || !Array.isArray(comp.content?.options)) {
    return null;
  }
  const labels = [];
  const schemaKeys = new Set();
  for (const opt of comp.content.options) {
    if (!opt) continue;
    const optKey = opt.key != null ? opt.key : opt.id;
    if (optKey == null) continue;
    schemaKeys.add(String(optKey));
    if (value[String(optKey)] === true) {
      labels.push(getCheckboxOptionLabel(fieldKey, String(optKey), index));
    }
  }
  const orphanKeys = Object.entries(value)
    .filter(([k, on]) => on === true && !schemaKeys.has(String(k)))
    .map(([k]) => k)
    .sort();
  for (const k of orphanKeys) {
    labels.push(getCheckboxOptionLabel(fieldKey, k, index));
  }
  return labels.length > 0 ? labels.join(", ") : "";
}

/**
 * Table / text display for one event cell.
 */
export function formatDataEventCellDisplay(fieldKey, value, index) {
  if (value === null || value === undefined) return "";
  if (typeof value === "object" && !Array.isArray(value)) {
    const comp = index?.get(String(fieldKey));
    if (comp?.type === "checkbox") {
      const ordered = formatCheckboxSelectionForDisplay(fieldKey, value, index);
      if (ordered !== null) return ordered;
    }
    return JSON.stringify(value);
  }
  return String(value);
}
